import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { useI18n } from '../../i18n';

export function RaffleWinnersInfo() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const token = user?.api_token ? `Bearer ${user.api_token}` : '';
  const [winners, setWinners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const loadWinners = useCallback(async () => {
    if (!token) {
      setWinners([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const res = await fetch('/api/v1/raffles/winners?limit=6', {
        method: 'GET',
        headers: { Authorization: token },
      });
      if (!res.ok) {
        throw new Error(t('Ошибка загрузки'));
      }
      const data = await res.json();
      if (data.status !== 'success') {
        throw new Error(data.error || t('Ошибка загрузки'));
      }
      setWinners(Array.isArray(data.data?.items) ? data.data.items : []);
    } catch (e) {
      setWinners([]);
      setSnackbar({ open: true, message: e.message || t('Ошибка загрузки'), severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadWinners();
  }, [loadWinners]);

  const formatDateTime = value => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return new Intl.DateTimeFormat(lang === 'ru' ? 'ru-RU' : 'en-US', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  return (
    <Box sx={{ flex: '1 1 360px', minWidth: 320, display: 'flex', flexDirection: 'column' }}>
      <Paper sx={{ p: 3, borderRadius: 2, display: 'flex', flexDirection: 'column', flex: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>{t('Победители')}</Typography>
          <Button size="small" variant="outlined" onClick={loadWinners} disabled={loading}>
            {t('Обновить')}
          </Button>
        </Box>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            {winners.length === 0 ? (
              <Typography sx={{ color: '#666', flexGrow: 1 }}>{t('Победители пока не зарегистрированы.')}</Typography>
            ) : (
              <TableContainer sx={{ flexGrow: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>{t('Билет')}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{t('Приз')}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{t('Дата')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {winners.map((item, index) => {
                      const rowId = item.id || `${item.ticket_code || 'ticket'}-${item.awarded_at || index}`;
                      return (
                        <TableRow key={rowId} hover>
                          <TableCell>{item.ticket_code || '—'}</TableCell>
                          <TableCell>{item.user_email || '—'}</TableCell>
                          <TableCell>{item.prize || '—'}</TableCell>
                          <TableCell>{formatDateTime(item.awarded_at)}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </Paper>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

